import _ from "lodash";
import React from "react";
import { useMappedState } from "redux-react-hook";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import styled from "styled-components";

import { Select } from "src/library/components/Forms";
import { IStore } from "src/core/shared/store";
import {
  IPrice,
  IPriceList,
  ListPriceChangeParams,
  priceChangeApi,
  PriceChangeDates
} from "src/core/shared/api/price-change.api";
import { IProduct } from "src/core/products/api/products.api";
import Table, { ITableInstance } from "src/library/components/Tables/Table";
import { priceChangeProductsTableColumns } from "src/core/shared/components/tables";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { Text } from "src/library/components/Text";
import Grid from "src/library/components/Hedron";
import { PageProps } from "src/pages/Router/routes";

import { ListProductsFilters } from "../../products/api/products.api";
import NoPriceChangeDataFound from "./NoPriceChangeDataFound";

export interface PriceChangeProductsTabProps {
  effectiveDate: string | string[] | null | undefined;
  priceCode: IPriceList | null;
}

/**
 * displays
 *   - a list of products by selected active price change date
 *   - a select component to navigate between price change dates
 */
const PriceChangeProductsTab: React.ReactFCWithChildren<PageProps<PriceChangeProductsTabProps>> = ({
  effectiveDate,
  priceCode,
  ...props
}) => {
  const { history, terminology } = props;
  const { installedModules } = useMappedState(({ global }: IStore) => ({ installedModules: global.settings ? global.settings.installedModules || [] : [] }));
  const [ listPriceChangeDatesResponse, listPriceChangeDatesRequest ] = priceChangeApi<PriceChangeDates>("GET_PRICE_CHANGE_DATES");
  const [ listPriceChangeProductsResponse, listPriceChangeProductsRequest ] = priceChangeApi<IPrice[]>("GET_PRICE_CHANGE_PRODUCTS");
  const [ pageFilters, setPageFilters ] = React.useState<{ effectiveDate: string; priceCodeId: number } | null>(null);
  const [ , setFilters ] = React.useState<ListProductsFilters>({});
  const TableRef = React.useRef<ITableInstance<IProduct>>(null);
  const priceCodeId = priceCode?.id || null;

  /** list price change dates by priceCodeId */
  React.useEffect(() => {
    if (priceCodeId) {
      listPriceChangeDatesRequest({ priceListId: priceCodeId });
    }
  }, [ priceCodeId ]);

  React.useEffect(() => {
    if (pageFilters) {
      listPriceChangeProductsRequest<ListPriceChangeParams>({ params: pageFilters });
    }
  }, [ pageFilters ]);

  /** The fetch for products by price and effective date to populate the table */
  const onFetchData = React.useCallback(
    (filters: ListProductsFilters) => {
      setFilters(filters);
    },
    [ ]
  );

  /** Reload the table  */
  React.useEffect(() => {
    if (pageFilters) {
      if (TableRef.current) {
        TableRef.current.reloadTable(true);
      }
    }
  }, [ pageFilters, TableRef ]);

  /** handle price change dates response */
  React.useEffect(() => {
    if (listPriceChangeDatesResponse.data && listPriceChangeDatesResponse.data.length) {
      // set active to be first if not set in url already
      if (!effectiveDate) {
        history.push(`?effectiveDate=${listPriceChangeDatesResponse.data[ listPriceChangeDatesResponse.data.length - 1 ].effectiveDate}`);
      }
    }

    if (listPriceChangeDatesResponse.error) {
      toast.error(listPriceChangeDatesResponse.error.message);
    }
  }, [ listPriceChangeDatesResponse ]);

  /** handle price changes to the priceCodeId and the date */
  React.useEffect(() => {
    if (effectiveDate && priceCodeId) {
      setPageFilters({
        effectiveDate: effectiveDate as string,
        priceCodeId
      });
    }

    // if remove date fron url
    if (!effectiveDate && priceCodeId) {
      listPriceChangeDatesRequest({ priceListId: priceCodeId });
    }
  }, [ effectiveDate, priceCodeId ]);

  const getProductTableColumns = React.useCallback(() => {
    return priceChangeProductsTableColumns(
      {
        prices: listPriceChangeProductsResponse.data ? listPriceChangeProductsResponse.data : [],
        priceCodeId,
        terminology,
        currency: priceCode?.currency
      },
      installedModules
    );
  }, [
    listPriceChangeProductsResponse.data,
    priceCodeId,
    terminology,
    priceCode?.currency,
    installedModules
  ]);

  // return loader if request price change dates requests pending
  if (!listPriceChangeDatesResponse.complete || listPriceChangeDatesResponse.pending) {
    return (
      <StandAloneLoaderWrapper>
        <LoadingSpinner type="circular" />
      </StandAloneLoaderWrapper>
    );
  }

  if (!effectiveDate) {
    return (
      <NoPriceChangeDataFound
        name={priceCode?.name}
        code={priceCode?.code}
      />
    );
  }

  return (
    <>
      {listPriceChangeDatesResponse.data && listPriceChangeDatesResponse.data.length === 0 ?
        (
          <NoPriceChangeDataFound
            name={priceCode?.name}
            code={priceCode?.code}
          />
        ) : (
          <>
            <Select
              as="select"
              label="Active Price Change Dates"
              name="active-price-change-dates"
              value={`/admin/products/price-lists/${priceCodeId}/changes?effectiveDate=${effectiveDate}`}
              handleChange={e => history.push(e.target.value)}
              options={_.map(listPriceChangeDatesResponse.data, priceChangeDate => ({
                text: moment.utc(priceChangeDate.effectiveDate, "YYYY-MM-DD").format("ddd D MMM YYYY"),
                value: `/admin/products/price-lists/${priceChangeDate.priceCodeId}/changes?effectiveDate=${priceChangeDate.effectiveDate}`
              }))}
            />

            <Grid.Box className="page-desc">
              <Text as="h4">
                {`Showing price changes as they were on ${moment
                  .utc(effectiveDate as string)
                  .format("ddd D MMM YYYY")}`}
              </Text>
            </Grid.Box>

            <Grid.Bounds className="table-content">

              <Grid.Box width="95%">
                {pageFilters && (
                  <>
                    {listPriceChangeProductsResponse.pending ? (
                      <LoadingSpinner
                        type="flat"
                        position="inline"
                      />
                    ) : (
                      <Table
                        tableId={`priceListId:${priceCodeId}`}
                        ref={TableRef}
                        tableOf={`${terminology.product.singular.text} prices`}
                        data={listPriceChangeProductsResponse.data ? listPriceChangeProductsResponse.data : []}
                        columns={getProductTableColumns()}
                        loading={listPriceChangeProductsResponse.pending}
                        error={listPriceChangeProductsResponse.error}
                        onFetchData={onFetchData}
                        pages={1}
                        totalRows={listPriceChangeProductsResponse.data ? listPriceChangeProductsResponse.data.length : 0}
                      />
                    )}

                  </>
                )}
              </Grid.Box>
            </Grid.Bounds>
          </>
        )}
    </>
  );
};

const StandAloneLoaderWrapper = styled.div`
   min-height: 300px; 
`;

export default PriceChangeProductsTab;
